/**
 * Phase 2C.2 — Version Sidebar + DiffViewer + RestorePreview + SnapshotDialog.
 *
 * Composition:
 *   <VersionSidebar/>        — right-side Sheet listing all snapshots+auto-saves
 *      ↳ SnapshotRow         — kind icon · name · notes · favorite · actions
 *      ↳ SnapshotDialog      — name + notes input for "Save snapshot…"
 *      ↳ DiffViewer          — side-by-side panel comparing any two refs
 *      ↳ RestoreConfirm      — restore preview showing the diff that will apply
 *
 * Architecture is timeline-aware: each row carries `parent_snapshot_id` +
 * `restored_from_snapshot_id`, so a future Timeline view can render lineage
 * as a graph without any data-model migration.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, History, Star, StarOff, Camera, FileText, Sparkles, RotateCcw,
  GitBranch, Trash2, ChevronRight, MoreHorizontal, ArrowRightLeft, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { bizDateTime } from '@/lib/dates';

const KIND_META = {
  manual:    { Icon: Camera,    label: 'Manual snapshot',    tone: 'text-primary',  badge: 'bg-primary/10 text-primary border-primary/30' },
  published: { Icon: Sparkles,  label: 'Published version',  tone: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/40' },
  restored:  { Icon: RotateCcw, label: 'Restored',           tone: 'text-amber-600', badge: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/40' },
  auto:      { Icon: GitBranch, label: 'Auto save',          tone: 'text-muted-foreground', badge: 'bg-muted text-muted-foreground border-border' },
};

const fmtTime = (iso, lang) => {
  if (!iso) return '';
  try { return bizDateTime(iso, lang); } catch { return iso; }
};

// Auto-save summaries are persisted in English ("N section(s), N content item(s)").
// Presentation-only Spanish rendering; stored data untouched.
const fmtSummary = (summary, isEs) => {
  if (!summary || !isEs) return summary;
  return summary
    .replace(/(\d+)\s*section\(s\)/g, '$1 sección(es)')
    .replace(/(\d+)\s*content item\(s\)/g, '$1 elemento(s) de contenido');
};

/* ============================================================================
 *                       VERSION SIDEBAR (top-level Sheet)
 * ========================================================================== */
export function VersionSidebar({ open, onOpenChange, assetId, adminApi, onAfterRestore }) {
  const { t } = useTranslation();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snapshotDialog, setSnapshotDialog] = useState(null);  // null | {} | {row}
  const [compareWith, setCompareWith] = useState(null);  // {sourceRef, sourceLabel}
  const [diffOpen, setDiffOpen] = useState(null);        // {sourceRef, targetRef, sourceLabel, targetLabel}
  const [restoreConfirm, setRestoreConfirm] = useState(null);

  const refresh = async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const out = await adminApi.studioListVersions(assetId, { include_auto: true });
      setVersions(out.versions || []);
    } catch (err) {
      toast.error(t('studio.versions.loadError', { defaultValue: 'Could not load versions.' }));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) refresh(); }, [open, assetId]); 

  const handleSaveSnapshot = async ({ name, notes }) => {
    try {
      await adminApi.studioCreateSnapshot(assetId, { name, notes });
      toast.success(t('studio.versions.snapshotSaved', { defaultValue: 'Snapshot saved.' }));
      setSnapshotDialog(null);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error', { defaultValue: 'Something went wrong.' }));
    }
  };

  const handleToggleFavorite = async (row) => {
    try {
      await adminApi.studioUpdateSnapshot(assetId, row.id, { is_favorite: !row.is_favorite });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(t('studio.versions.confirmDelete', {
      defaultValue: 'Delete this snapshot?',
    }))) return;
    try {
      await adminApi.studioDeleteSnapshot(assetId, row.id, row.is_favorite);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const handleCompareTrigger = (row) => {
    if (!compareWith) {
      setCompareWith({ sourceRef: row.id, sourceLabel: row.name || `${row.kind} · ${row.id.slice(0, 6)}` });
      toast.message(t('studio.versions.compareSecond', {
        defaultValue: 'Now select a second snapshot to compare against.',
      }));
    } else {
      // Open the diff viewer
      setDiffOpen({
        sourceRef: compareWith.sourceRef,
        sourceLabel: compareWith.sourceLabel,
        targetRef: row.id,
        targetLabel: row.name || `${row.kind} · ${row.id.slice(0, 6)}`,
      });
      setCompareWith(null);
    }
  };

  const handleCompareToCurrent = (row) => {
    setDiffOpen({
      sourceRef: row.id,
      sourceLabel: row.name || `${row.kind} · ${row.id.slice(0, 6)}`,
      targetRef: 'current',
      targetLabel: t('studio.versions.current', { defaultValue: 'Current draft' }),
    });
  };

  const handleRestoreTrigger = (row) => {
    if (row.kind === 'auto') return;  // not restorable
    setRestoreConfirm(row);
  };

  const handleRestoreConfirmed = async () => {
    if (!restoreConfirm) return;
    try {
      await adminApi.studioRestore(assetId, restoreConfirm.id);
      toast.success(t('studio.versions.restoreSuccess', {
        defaultValue: 'Restored. A "Restored" snapshot of the previous state was also created.',
      }));
      setRestoreConfirm(null);
      onOpenChange(false);
      if (onAfterRestore) onAfterRestore();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error', { defaultValue: 'Something went wrong.' }));
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md flex flex-col p-0"
          data-testid="studio-version-sidebar"
        >
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
            <div className="flex items-start justify-between gap-3 pr-8">
              <div className="min-w-0">
                <SheetTitle className="text-base inline-flex items-center gap-2">
                  <History className="h-4 w-4" />
                  {t('studio.versions.title', { defaultValue: 'Versions' })}
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {t('studio.versions.subtitle', {
                    defaultValue: 'Snapshots, restores, and recent auto-saves.',
                  })}
                </SheetDescription>
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={() => setSnapshotDialog({})}
                data-testid="studio-version-snapshot-button"
                className="h-8 text-xs gap-1"
              >
                <Camera className="h-3 w-3" />
                {t('studio.versions.takeSnapshot', { defaultValue: 'Take snapshot' })}
              </Button>
            </div>
            {compareWith && (
              <div
                className="mt-2 rounded-md bg-primary/10 border border-primary/30 px-3 py-2 text-xs flex items-center justify-between gap-2"
                data-testid="studio-version-compare-banner"
              >
                <span>
                  <ArrowRightLeft className="h-3 w-3 inline mr-1" />
                  {t('studio.versions.compareBanner', {
                    defaultValue: 'Comparing with "{{label}}". Pick a second row.',
                    label: compareWith.sourceLabel,
                  })}
                </span>
                <button
                  type="button"
                  onClick={() => setCompareWith(null)}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="studio-version-compare-cancel"
                >
                  ×
                </button>
              </div>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-2 py-2">
            {loading && versions.length === 0 && (
              <div className="text-center py-12" data-testid="studio-version-loading">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
              </div>
            )}
            {!loading && versions.length === 0 && (
              <div className="text-center py-12" data-testid="studio-version-empty">
                <p className="text-xs text-muted-foreground">
                  {t('studio.versions.empty', { defaultValue: 'No versions yet. Take a snapshot to begin.' })}
                </p>
              </div>
            )}
            <ul className="space-y-1" data-testid="studio-version-list">
              {versions.map((v) => (
                <SnapshotRow
                  key={v.id || v.save_id_ref}
                  row={v}
                  onCompare={handleCompareTrigger}
                  onCompareCurrent={handleCompareToCurrent}
                  onToggleFavorite={handleToggleFavorite}
                  onRestore={handleRestoreTrigger}
                  onDelete={handleDelete}
                  isCompareSelected={compareWith?.sourceRef === v.id}
                />
              ))}
            </ul>
          </div>
        </SheetContent>
      </Sheet>

      {snapshotDialog && (
        <SnapshotDialog
          onClose={() => setSnapshotDialog(null)}
          onSave={handleSaveSnapshot}
        />
      )}
      {diffOpen && (
        <DiffViewer
          open
          onOpenChange={(v) => !v && setDiffOpen(null)}
          assetId={assetId}
          adminApi={adminApi}
          sourceRef={diffOpen.sourceRef}
          sourceLabel={diffOpen.sourceLabel}
          targetRef={diffOpen.targetRef}
          targetLabel={diffOpen.targetLabel}
        />
      )}
      {restoreConfirm && (
        <RestoreConfirm
          row={restoreConfirm}
          assetId={assetId}
          adminApi={adminApi}
          onCancel={() => setRestoreConfirm(null)}
          onConfirm={handleRestoreConfirmed}
        />
      )}
    </>
  );
}

/* ============================================================================
 *                                  ROW
 * ========================================================================== */
function SnapshotRow({ row, onCompare, onCompareCurrent, onToggleFavorite, onRestore, onDelete, isCompareSelected }) {
  const { t, i18n } = useTranslation();
  const isEs = (i18n.language || '').startsWith('es');
  const meta = KIND_META[row.kind] || KIND_META.manual;
  const KindIcon = meta.Icon;
  const isAuto = row.kind === 'auto';
  const restorable = !isAuto;
  return (
    <li
      data-testid={`studio-version-row-${row.id || row.save_id_ref}`}
      data-kind={row.kind}
      className={`rounded-md border ${isCompareSelected ? 'border-primary bg-primary/5' : 'border-border bg-card'} p-2.5 hover:shadow-sm transition-shadow`}
    >
      <div className="flex items-start gap-2">
        <div className={`rounded-md bg-muted/40 p-1.5 shrink-0 ${meta.tone}`}>
          <KindIcon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <Badge variant="outline" className={`text-xs h-4 px-1 ${meta.badge}`}>
              {t(`studio.versions.kind.${row.kind}`, { defaultValue: meta.label })}
            </Badge>
            {row.is_favorite && (
              <Star className="h-3 w-3 fill-amber-400 text-amber-500" data-testid="studio-version-favorite-marker" />
            )}
          </div>
          <p className="text-xs font-medium mt-1 truncate">
            {row.name || (isAuto ? (fmtSummary(row.summary, isEs) || t('studio.versions.unnamed', { defaultValue: 'Auto save' })) : t('studio.versions.unnamed', { defaultValue: 'Unnamed snapshot' }))}
          </p>
          {row.notes && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{row.notes}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1 font-mono">
            {fmtTime(row.created_at, i18n.language)} · {row.created_by_name || row.created_by?.slice(0, 8) || '—'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 mt-2 justify-end" data-testid={`studio-version-row-actions-${row.id || row.save_id_ref}`}>
        {!isAuto && (
          <Button
            variant="ghost" size="sm" className="h-6 px-2 text-xs"
            onClick={() => onToggleFavorite(row)}
            data-testid={`studio-version-favorite-${row.id}`}
            title={row.is_favorite ? t('studio.versions.unfavorite', { defaultValue: 'Unfavorite' }) : t('studio.versions.favorite', { defaultValue: 'Favorite' })}
          >
            {row.is_favorite ? <StarOff className="h-3 w-3" /> : <Star className="h-3 w-3" />}
          </Button>
        )}
        <Button
          variant="ghost" size="sm" className="h-6 px-2 text-xs"
          onClick={() => onCompareCurrent(row)}
          data-testid={`studio-version-diff-current-${row.id || row.save_id_ref}`}
          title={t('studio.versions.compareCurrent', { defaultValue: 'Compare with current' })}
        >
          <ArrowRightLeft className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost" size="sm" className="h-6 px-2 text-xs"
          onClick={() => onCompare(row)}
          data-testid={`studio-version-compare-${row.id || row.save_id_ref}`}
          title={t('studio.versions.compareOther', { defaultValue: 'Compare with another snapshot' })}
        >
          <GitBranch className="h-3 w-3" />
        </Button>
        {restorable && (
          <Button
            variant="outline" size="sm" className="h-6 px-2 text-xs gap-1"
            onClick={() => onRestore(row)}
            data-testid={`studio-version-restore-${row.id}`}
          >
            <RotateCcw className="h-3 w-3" />
            {t('studio.versions.restore', { defaultValue: 'Restore' })}
          </Button>
        )}
        {!isAuto && row.kind !== 'published' && (
          <Button
            variant="ghost" size="sm" className="h-6 px-2 text-xs text-destructive"
            onClick={() => onDelete(row)}
            data-testid={`studio-version-delete-${row.id}`}
            title={t('common.delete', { defaultValue: 'Delete' })}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    </li>
  );
}

/* ============================================================================
 *                              SNAPSHOT DIALOG
 * ========================================================================== */
function SnapshotDialog({ onClose, onSave }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) {
      toast.error(t('studio.versions.nameRequired', { defaultValue: 'Name is required.' }));
      return;
    }
    setSaving(true);
    await onSave({ name: name.trim(), notes: notes.trim() });
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="studio-snapshot-dialog">
        <DialogHeader>
          <DialogTitle>{t('studio.versions.dialogTitle', { defaultValue: 'Save a snapshot' })}</DialogTitle>
          <DialogDescription>
            {t('studio.versions.dialogDesc', { defaultValue: 'Lock the current state under a name so you can return to it later.' })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 py-2">
          <div>
            <label className="text-xs font-medium block mb-1">
              {t('studio.versions.fieldName', { defaultValue: 'Name' })}
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('studio.versions.namePlaceholder', { defaultValue: 'e.g. Pre-launch v1' })}
              data-testid="studio-snapshot-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">
              {t('studio.versions.fieldNotes', { defaultValue: 'Notes (optional)' })}
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('studio.versions.notesPlaceholder', { defaultValue: 'Why are you taking this snapshot? Who reviewed it?' })}
              data-testid="studio-snapshot-notes-input"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="studio-snapshot-cancel" type="button">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" disabled={saving} data-testid="studio-snapshot-save">
              {saving && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              {t('studio.versions.dialogSave', { defaultValue: 'Save snapshot' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================
 *                              DIFF VIEWER
 * ========================================================================== */
function DiffViewer({ open, onOpenChange, assetId, adminApi, sourceRef, sourceLabel, targetRef, targetLabel }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const out = await adminApi.studioDiff(assetId, { source_ref: sourceRef, target_ref: targetRef });
        if (alive) setData(out);
      } catch (err) {
        toast.error('Diff failed');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [assetId, sourceRef, targetRef]); 

  const diff = data?.diff;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0" data-testid="studio-diff-viewer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <SheetTitle className="text-base inline-flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4" />
            {t('studio.diff.title', { defaultValue: 'Compare versions' })}
          </SheetTitle>
          <SheetDescription className="text-xs">
            <span className="font-mono">{sourceLabel}</span>
            <ChevronRight className="h-3 w-3 inline mx-1" />
            <span className="font-mono">{targetLabel}</span>
          </SheetDescription>
          {diff && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="studio-diff-totals">
              <DiffPill n={diff.totals.questions_added} label="added" color="emerald" testId="studio-diff-added" />
              <DiffPill n={diff.totals.questions_modified} label="modified" color="amber" testId="studio-diff-modified" />
              <DiffPill n={diff.totals.questions_removed} label="removed" color="red" testId="studio-diff-removed" />
              <DiffPill n={diff.totals.sections_added} label="sec+" color="emerald" testId="studio-diff-sec-added" />
              <DiffPill n={diff.totals.sections_removed} label="sec-" color="red" testId="studio-diff-sec-removed" />
              <DiffPill n={diff.totals.sections_renamed} label="sec→" color="amber" testId="studio-diff-sec-renamed" />
              <DiffPill n={diff.totals.meta_changed} label="meta" color="primary" testId="studio-diff-meta" />
            </div>
          )}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && (
            <div className="text-center py-12">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
            </div>
          )}
          {!loading && diff && (
            <DiffSections diff={diff} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DiffPill({ n, label, color, testId }) {
  if (!n) return null;
  const tone = {
    emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/40',
    amber: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/40',
    red: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-500/15 dark:text-red-200 dark:border-red-500/40',
    primary: 'bg-primary/10 text-primary border-primary/30',
  }[color];
  return (
    <Badge variant="outline" className={`text-xs h-5 px-1.5 ${tone}`} data-testid={testId}>
      {n} {label}
    </Badge>
  );
}

function DiffSections({ diff }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {diff.meta_changes?.length > 0 && (
        <section data-testid="studio-diff-section-meta">
          <h4 className="text-xs font-semibold mb-2">
            {t('studio.diff.meta', { defaultValue: 'Metadata changes' })}
          </h4>
          <ul className="space-y-1">
            {diff.meta_changes.map((c) => (
              <li key={c.key} className="text-xs rounded border border-border bg-muted/30 px-2 py-1.5 grid grid-cols-3 gap-2">
                <span className="font-mono text-xs">{c.key}</span>
                <span className="text-destructive truncate">{String(c.before ?? '—')}</span>
                <span className="text-success truncate">{String(c.after ?? '—')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {diff.question_changes?.added?.length > 0 && (
        <DiffQuestionGroup label={t('studio.diff.qAdded', { defaultValue: 'Content added' })}
          rows={diff.question_changes.added} tone="emerald" testId="studio-diff-q-added" />
      )}
      {diff.question_changes?.modified?.length > 0 && (
        <DiffQuestionGroup label={t('studio.diff.qModified', { defaultValue: 'Content modified' })}
          rows={diff.question_changes.modified} tone="amber" testId="studio-diff-q-modified" showChanges />
      )}
      {diff.question_changes?.removed?.length > 0 && (
        <DiffQuestionGroup label={t('studio.diff.qRemoved', { defaultValue: 'Content removed' })}
          rows={diff.question_changes.removed} tone="red" testId="studio-diff-q-removed" />
      )}
      {diff.section_changes?.added?.length > 0 && (
        <DiffQuestionGroup label={t('studio.diff.secAdded', { defaultValue: 'Sections added' })}
          rows={diff.section_changes.added.map((s) => ({ ...s, prompt: s.name }))} tone="emerald" testId="studio-diff-sec-added-list" />
      )}
      {diff.section_changes?.removed?.length > 0 && (
        <DiffQuestionGroup label={t('studio.diff.secRemoved', { defaultValue: 'Sections removed' })}
          rows={diff.section_changes.removed.map((s) => ({ ...s, prompt: s.name }))} tone="red" testId="studio-diff-sec-removed-list" />
      )}
    </div>
  );
}

function DiffQuestionGroup({ label, rows, tone, testId, showChanges }) {
  const toneCls = {
    emerald: 'border-l-emerald-500',
    amber:   'border-l-amber-500',
    red:     'border-l-red-500',
  }[tone];
  return (
    <section data-testid={testId}>
      <h4 className="text-xs font-semibold mb-2">{label} <span className="text-muted-foreground">({rows.length})</span></h4>
      <ul className="space-y-1">
        {rows.map((q) => (
          <li
            key={q.id}
            className={`text-xs rounded border-l-2 border-border bg-card pl-2 py-1.5 pr-2 ${toneCls}`}
          >
            <p className="font-medium truncate">
              {q.prompt || q.name || '—'}
              {q.stable_id && (
                <span className="font-mono text-xs ml-1.5 px-1 py-0.5 rounded bg-muted">{q.stable_id}</span>
              )}
            </p>
            {showChanges && q.changes && (
              <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                {q.changes.map((ch, idx) => (
                  <li key={idx} className="font-mono truncate">
                    {ch.field}: <span className="text-destructive">{JSON.stringify(ch.before)}</span> → <span className="text-success">{JSON.stringify(ch.after)}</span>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ============================================================================
 *                              RESTORE CONFIRM
 * ========================================================================== */
function RestoreConfirm({ row, assetId, adminApi, onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const out = await adminApi.studioDiff(assetId, {
          source_ref: 'current', target_ref: row.id,
        });
        if (alive) setData(out);
      } catch (err) {
        toast.error('Could not load restore preview');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [assetId, row.id]); 

  const diff = data?.diff;
  const dangerous = diff && (diff.totals.questions_removed > 0 || diff.totals.sections_removed > 0);

  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="studio-restore-confirm">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <RotateCcw className="h-4 w-4" />
            {t('studio.versions.restoreTitle', {
              defaultValue: 'Restore "{{name}}"?',
              name: row.name || `${row.kind} · ${row.id.slice(0, 8)}`,
            })}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t('studio.versions.restoreDesc', {
              defaultValue: 'This will rewind the current draft. A "Restored" snapshot of the current state is saved automatically so this is reversible.',
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          {loading && <Loader2 className="h-5 w-5 animate-spin mx-auto" />}
          {!loading && diff && (
            <div className="space-y-2" data-testid="studio-restore-preview">
              <div className="flex flex-wrap gap-1.5">
                <DiffPill n={diff.totals.questions_added} label="added" color="emerald" testId="studio-restore-q-added" />
                <DiffPill n={diff.totals.questions_modified} label="modified" color="amber" testId="studio-restore-q-modified" />
                <DiffPill n={diff.totals.questions_removed} label="removed" color="red" testId="studio-restore-q-removed" />
                <DiffPill n={diff.totals.sections_added} label="sec+" color="emerald" testId="studio-restore-sec-added" />
                <DiffPill n={diff.totals.sections_removed} label="sec-" color="red" testId="studio-restore-sec-removed" />
                <DiffPill n={diff.totals.meta_changed} label="meta" color="primary" testId="studio-restore-meta" />
              </div>
              {dangerous && (
                <div className="rounded-md bg-amber-100 border border-amber-300 dark:bg-amber-500/15 dark:border-amber-500/40 px-3 py-2 text-xs flex items-start gap-2 mt-2" data-testid="studio-restore-warning">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
                  <span>{t('studio.versions.restoreWarn', {
                    defaultValue: 'This restore will remove content that exists today. The current state will be saved as a "Restored" snapshot.',
                  })}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="studio-restore-cancel">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={async () => { setRestoring(true); await onConfirm(); setRestoring(false); }} disabled={loading || restoring} data-testid="studio-restore-confirm-button">
            {restoring && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            {t('studio.versions.restoreConfirm', { defaultValue: 'Restore' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
